import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef} from "react";
import StreamCountdown from "../../components/streaming/StreamCountdown";
import StreamStatusScreen, {
  StreamHeaderBar,
} from "../../components/streaming/StreamStatusScreen";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { usePoster } from "../../hooks/usePoster";
import { useAuth } from "../../context/AuthProvider";
import { socket } from "../../components/mediasoup/socket";
import { recordingSocket } from "../../components/mediasoup/recordingSocket";
import logger from "../../utils/logger";
import SecureStreamPlayer, {
  type StreamPlayerHandle,
} from "../../components/streaming/StreamPlayer";
import api from "../../lib/api";

interface StreamData {
  streamEndAt?: string;
  streamPath: string;
  streamStartAt: string;
  streamStatus: boolean;
  currentTime: number;
  highCourtName: string;
  courtName: string;
  /** Bench name, or court room name when the session is not on a bench */
  benchName?: string;
  courtRoomName?: string;
  status: string;
  streamingStatus: string;
}

interface WatchedByInfo {
  sessionId: string;
  userId: string | undefined;
  ip: string;
  latitude: any;
  longitude: any;
  date: Date;
}

interface VideoPlayerProps {
  cnr?: string;
  date?: string;
}

type ViewerStatus = "rec" | "scheduled" | "live" | "paused" | "draining";

/* The DB streamingStatus flips the moment the judge pauses / resumes, but
   viewers must only see it after the configured delay. The stream server
   tracks that delayed status per session; null = no active session. */
const getViewerStatus = (sessionId?: string) =>
  new Promise<ViewerStatus | null>((resolve) => {
    if (!sessionId || !recordingSocket.connected) return resolve(null);
    recordingSocket
      .timeout(3000)
      .emit("session_stream_status", { sessionId }, (err: any, res: any) =>
        resolve(err ? null : (res?.status ?? null)),
      );
  });

const VideoPlayer = ({ cnr, date: propDate }: VideoPlayerProps = {}) => {
  const { user, userLocation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const post = usePoster();

  const CNR = cnr || (location.state?.cnr as string | undefined);
  const date = propDate || (location.state?.date as string | undefined);

  const ip = user?.ip || "0.0.0.0";
  const mobile = user?.phone || "";

  const [watchedBy, setWatchedBy] = useState<WatchedByInfo | null>(null);
  const [data, setData] = useState<StreamData | null>(null);
  const [isLive, setIsLive] = useState(false);
  const [streamEnded, setStreamEnded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isLeft, setIsLeft] = useState(false);
  const [hlsToken, setHlsToken] = useState<string>("");

  const playerRef = useRef<StreamPlayerHandle>(null);
  const hasRunRef = useRef(false);

  /* ---------------------------------------------------------
      PAUSE / PLAY VIDEO ELEMENT WHEN isPaused CHANGES
      — must be before any return so hook order is stable
  --------------------------------------------------------- */
  useEffect(() => {
    if (!playerRef.current || !isLive) return;
    if (isPaused) {
      playerRef.current.pause(); // also mutes → kills audio leak
    } else {
      playerRef.current.play(); // unmutes + seeks to live edge
    }
  }, [isPaused, isLive]);

  /* ---------------------------------------------------------
      BUILD WATCHED-BY PAYLOAD
  --------------------------------------------------------- */
  useEffect(() => {
    if (hasRunRef.current || !user || !CNR || !ip || !userLocation) return;
    setWatchedBy({
      sessionId: CNR,
      userId: user.id,
      ip,
      latitude: userLocation.lat,
      longitude: userLocation.lon,
      date: date ? new Date(date) : new Date(),
    });
    hasRunRef.current = true;
  }, [user, CNR, ip, userLocation, date]);

  /* ---------------------------------------------------------
      FETCH STREAM STATUS
  --------------------------------------------------------- */
  // silent = refresh data without unmounting the player (no loading screen)
  const fetchStreamStatus = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [res, viewerStatus] = await Promise.all([
        post(`/api/search/streaming-status`, { cnr: CNR }),
        getViewerStatus(CNR),
      ]);
      logger.log("📡 Stream status response:", res, viewerStatus);
      if (res.status && res.data) {
        setData(res.data);
        setIsPaused(
          viewerStatus
            ? viewerStatus === "paused"
            : res.data.streamingStatus === "paused",
        );
      }
    } catch (err) {
      logger.error("❌ Error fetching stream:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [CNR, post]);

  const streamStartedRef = useRef(false);
  streamStartedRef.current = !!data?.streamStartAt;


useEffect(() => {
  if (!user?.id) return;

  const fetchToken = () => {
    api.get("/api/search/stream/hls-token")
      .then((res) => setHlsToken(res.data.token))
      .catch(console.error);
  };

  fetchToken(); // immediate
  const interval = setInterval(fetchToken, 30 * 60 * 1000); // refresh every 30min
  return () => clearInterval(interval);
}, [user?.id]);

  useEffect(() => {
    if (!CNR) {
      navigate(-1);
      return;
    }
    fetchStreamStatus();
  }, [CNR, fetchStreamStatus, navigate]);

// const securedStreamPath = data?.streamPath || "";

  /* ---------------------------------------------------------
      STREAM TIMER LOGIC
  --------------------------------------------------------- */
  useEffect(() => {
    // streamEndAt is null while the stream is running — only set on stop
    if (!data?.streamStartAt) return;
    if (data.status !== "rec+streaming" && !data.streamEndAt) return;

    const startTime = new Date(data.streamStartAt).getTime();
    const endTime = data.streamEndAt
      ? new Date(data.streamEndAt).getTime()
      : Infinity;

    const check = () => {
      const now = Date.now();
      if (now >= endTime) {
        setStreamEnded(true);
        setIsLive(false);
      } else if (now >= startTime) {
        setIsLive(true);
      } else {
        setIsLive(false);
      }
    };

    check();
    const interval = setInterval(check, 2000);
    return () => clearInterval(interval);
  }, [data]);

  /* ---------------------------------------------------------
      SOCKET STREAM EVENTS
  --------------------------------------------------------- */
  useEffect(() => {
    if (!socket) return;

    const onStatus = ({ sessionId, status, streamingStatus }: any) => {
      if (sessionId !== CNR) return;

      // Pause — show overlay, mute player immediately
      if (streamingStatus === "paused" || status === "paused") {
        setIsPaused(true);
        return;
      }

      // Resume — lift overlay, unmute + seek to live edge
      // Both fields present distinguishes resume from initial "live" start
      if (status === "live" && streamingStatus === "live") {
        setIsPaused(false);
        return;
      }

      // Resume: keep the pause overlay until the delay window has played
      // out — the server sends "live" then
      if (status === "scheduled" && streamStartedRef.current) return;

      // Stop: keep playing the delayed tail — refetch only picks up
      // streamEndAt, the timer ends the stream when it is reached
      if (status === "draining") {
        fetchStreamStatus(true);
        return;
      }

      // All other status changes (initial start, delayed "live")
      // → refetch full data so timer/isLive recalculates correctly
      setStreamEnded(false);
      setIsLive(false);
      fetchStreamStatus();
    };

    const onStop = ({ sessionId }: any) => {
      if (sessionId === CNR) {
        setStreamEnded(true);
        setIsLive(false);
      }
    };

    recordingSocket.on("stream-status", onStatus);
    // sent by the stream server once the delayed tail has drained — the
    // meeting socket's stream_stop fires on recording stop, without delay
    recordingSocket.on("stream_stop", onStop);

    return () => {
      recordingSocket.off("stream-status", onStatus);
      recordingSocket.off("stream_stop", onStop);
    };
  }, [socket, CNR, fetchStreamStatus]);

  /* ---------------------------------------------------------
      LEFT-BY ON PAGE UNLOAD
  --------------------------------------------------------- */
  useEffect(() => {
    if (!socket || !CNR || !user?.id) return;
    const payload = { sessionId: CNR, userId: user?.id };
    const handleUnload = () => {
      socket.emit("leftBy", payload);
      setIsLeft(true);
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [socket, user]);

  /* ---------------------------------------------------------
      EMIT LEFT-BY ON ROUTE CHANGE
  --------------------------------------------------------- */
  useEffect(() => {
    return () => {
      if (socket && CNR && user?.id) {
        socket.emit("leftBy", { sessionId: CNR, userId: user.id });
        logger.log("🚪 LEFT BY SENT ON ROUTE CHANGE");
        setIsLeft(true);
      }
    };
  }, [socket, CNR, user]);

  useEffect(() => {
    if (!socket || !isLive || !watchedBy) return;
    socket.emit("watchedBy", watchedBy);
  }, [socket, isLive, watchedBy, isLeft]);

  console.log("hls token....",hlsToken);
  console.log("dataelekekle",data);

  /* ---------------------------------------------------------
      RENDER GUARDS
  --------------------------------------------------------- */
  // Same label the public viewer shows: bench name, else court room name
  const courtLabel =
    data?.benchName ||
    data?.courtRoomName ||
    data?.courtName ||
    data?.highCourtName ||
    undefined;
  const matterLabel = CNR ? `CNR: ${CNR}` : undefined;

  if (loading)
    return (
      <StreamStatusScreen
        status="loading"
        courtLabel={courtLabel}
        matterLabel={matterLabel}
      />
    );

  if (!data?.streamStatus)
    return (
      <StreamStatusScreen
        status="unavailable"
        courtLabel={courtLabel}
        matterLabel={matterLabel}
        action={{
          label: "Retry",
          icon: RefreshCw,
          onClick: () => window.location.reload(),
        }}
      />
    );
  if (streamEnded)
    return (
      <StreamStatusScreen
        status="concluded"
        courtLabel={courtLabel}
        matterLabel={matterLabel}
        action={{ label: "Go back", icon: ArrowLeft, onClick: () => navigate(-1) }}
      />
    );
  if (!data.streamStartAt)
    return (
      <StreamStatusScreen
        status="notStarted"
        courtLabel={courtLabel}
        matterLabel={matterLabel}
        action={{
          label: "Refresh",
          icon: RefreshCw,
          onClick: () => window.location.reload(),
        }}
      />
    );

  /* ---------------------------------------------------------
      MAIN RENDER
  --------------------------------------------------------- */


  return (
    <div className="w-full h-full">
      {isLive ? (
        <div className="flex h-full w-full flex-col bg-black">
          <StreamHeaderBar
            courtLabel={courtLabel}
            matterLabel={matterLabel}
          />
          {/* centres the 16:9 player vertically on mobile portrait */}
          <div className="relative flex min-h-0 w-full flex-1 items-center">
          {/*
            Player stays ALWAYS mounted when isLive=true.
            Overlay sits on top — no unmount = no HLS buffer loss.
          */}
                <SecureStreamPlayer
        ref={playerRef}
        streamPath={data.streamPath} 
        hlsToken={hlsToken}            
        user={user?.name || ""}
        ip={ip}
        caseId={CNR || ""}
        mobile={mobile}
        courtName={courtLabel || ""}
        location={user?.location || ""}
        isPaused={isPaused}
        showStatusHeader={false}
      />
          </div>
        </div>
      ) : (
        <StreamCountdown
          streamStartAt={data.streamStartAt}
          onCountdownEnd={() => setIsLive(true)}
          courtLabel={courtLabel}
          matterLabel={matterLabel}
        />
      )}
    </div>
  );
};

export default VideoPlayer;
